import { Log, useProxyWatch, useProxyWatchUpdates, useTranslation } from '@fanfanlo';
import { 
    Box, 
    List, 
    ListItem, 
    ListItemAvatar, 
    ListItemText, 
    Avatar, 
    Typography, 
    ListItemSecondaryAction,
    IconButton,
    Chip
} from '@mui/material';
import { IMatrixRoomData, IMatrixRoomInfo, matrixRoom } from '../../../client/room/';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen'; // 导入新图标
import { matrixClient } from '../../../client';
import React, { useState } from 'react'; // 导入 useState
import { useRouter } from 'next/router';
import CreateShortcutDialog from './CreateShortcutDialog'; // 导入新组件

const fileLogger = new Log(false, 'im.components.rooms.summary-room-list.SummaryRoomList');
// fileLogger.pause = true;
// fileLogger.childrenPaused = true;

interface RoomListItemProps {
    room: IMatrixRoomInfo;
    onClick: (roomId: string) => void;
    onInviteResponse?: (roomId: string, accept: boolean) => Promise<void>;
    onOpenShortcutDialog: (roomId: string) => void; // 新增回调
}

function RoomListItem({ room, onClick, onInviteResponse, onOpenShortcutDialog }: RoomListItemProps) {
    const logger = fileLogger.sub(false, 'RoomListItem_ui');
    logger.pause = true;
    logger.log('room=', room);
    const [name] = useProxyWatch(room, 'name', room.name);
    const [avatarUrl] = useProxyWatch(room, 'avatarUrl', room.avatarUrl);
    const [isProcessing, setIsProcessing] = React.useState(false);
    const { t } = useTranslation('homepage/im/components/rooms/summary-room-list/content');

    const handleInviteResponse = async (e: React.MouseEvent, accept: boolean) => {
        e.stopPropagation();
        if (onInviteResponse && !isProcessing) {
            setIsProcessing(true);
            try {
                await onInviteResponse(room.roomId, accept);
            } finally {
                setIsProcessing(false);
            }
        }
    };

    const getRoomName = () => {
        if (!room.name) return room.roomId;
        return room.name;
    };

    // 用 useProxyWatch 监听 lastMessages
    const [lastMsg] = useProxyWatch(matrixRoom.roomData.lastMessages, [room.roomId], matrixRoom.roomData.lastMessages[room.roomId]);
    logger.log('lastMsg=', lastMsg);
    logger.log('lastMessages=', matrixRoom.roomData.lastMessages);
    let lastMsgSummary = '';
    if (lastMsg) {
        const { msgtype, body } = lastMsg.content || {};
        if (msgtype === 'm.text') lastMsgSummary = body;
        else if (msgtype === 'm.image') lastMsgSummary = '[图片]';
        else if (msgtype === 'm.file') lastMsgSummary = `[文件] ${body}`;
        else if (msgtype === 'm.video') lastMsgSummary = '[视频]';
        else if (msgtype === 'm.audio') lastMsgSummary = '[音频]';
        else if (msgtype === 'm.location') lastMsgSummary = '[位置]';
        else lastMsgSummary = '[消息]';
    }

    // @ts-ignore: membership 字段在 IMatrixRoomInfo 上
    const isInvite = room.membership === 'invite';

    return (
        <ListItem 
            onClick={() => onClick(room.roomId)}
            sx={{
                bgcolor: 'background.paper',
                opacity: isProcessing ? 0.7 : 1,
                pointerEvents: isProcessing ? 'none' : 'auto'
            }}
            secondaryAction={
                isInvite ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => handleInviteResponse(e, true)}
                            title={t('SummaryRoomList.acceptInvite')}
                            disabled={isProcessing}
                        >
                            <CheckIcon />
                        </IconButton>
                        <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => handleInviteResponse(e, false)}
                            title={t('SummaryRoomList.rejectInvite')}
                            disabled={isProcessing}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenShortcutDialog(room.roomId);
                            }}
                            title="创建桌面快捷方式"
                        >
                            <AddToHomeScreenIcon />
                        </IconButton>
                        {(room.unreadCount ?? 0) > 0 && (
                            <Chip 
                                label={room.unreadCount?.toString() ?? '0'} 
                                size="small" 
                                sx={{ 
                                    bgcolor: 'primary.main', 
                                    color: 'primary.contrastText',
                                    px: 1
                                }}
                            />
                        )}
                    </Box>
                )
            }
        >
            <ListItemAvatar>
                <Avatar src={avatarUrl}>
                    {name.charAt(0)}
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                primary={name}
                secondary={lastMsgSummary}
            />
        </ListItem>
    );
}


export function SummaryRoomList() {
  // ... (i18n and other hooks remain the same)
  const { t } = useTranslation('homepage/im/components/rooms/summary-room-list/content');
  const [rooms] = useProxyWatchUpdates(matrixRoom.roomData, 'joinedRooms', matrixRoom.roomData.joinedRooms)
  const router = useRouter();

  // 新增：管理弹窗状态
  const [shortcutDialogRoomId, setShortcutDialogRoomId] = useState<string | null>(null);

  const handleInviteResponse = async (roomId: string, accept: boolean) => {
    try {
      if (accept) {
        await matrixClient.client.joinRoom(roomId);
      } else {
        await matrixClient.client.leave(roomId);
      }
    } catch (error) {
      console.error('处理邀请时出错:', error);
    }
  };

  const handleRoomClick = (roomId: string) => {
    console.log('进入房间:', roomId);
    router.push(`/im/room?id=${encodeURIComponent(roomId)}`);
  };

  if (rooms.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
        {t('SummaryRoomList.noRooms')}
      </Box>
    );
  }

  const shortcutDialogRoom = rooms.find(r => r.roomId === shortcutDialogRoomId);

  return (
    <>
      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {rooms.map((room) => (
          <RoomListItem 
            key={room.roomId} 
            room={room} 
            onClick={handleRoomClick}
            onInviteResponse={handleInviteResponse}
            onOpenShortcutDialog={setShortcutDialogRoomId} // 传递回调
          />
        ))}
      </List>
      {shortcutDialogRoom && (
        <CreateShortcutDialog 
          room={shortcutDialogRoom}
          onClose={() => setShortcutDialogRoomId(null)}
        />
      )}
    </>
  );
}
